import type { JoyfulAppEnvironment } from '../../../utils/appContext'
import { CornerPositionSchema } from '../../../../core/components/cornerLayout/cornerPositionSchema'
import type { PhoneCallSourceType } from '../types'
import type { PhoneState } from '../utils/phoneState'
import { createNonNullableContext, createEnumSchema } from '@joyful/utils'
import type { UseMutationResult } from '@tanstack/react-query'
import type { Dispatch, SetStateAction } from 'react'
import { z } from 'zod'
export type { PhoneCallSourceType } from '../types'

export interface InitiatePhoneCallVariables {
  e164PhoneNumber: string
  encounterId?: string
  phoneCallTaskId?: string
  sourceId?: string
  sourceType?: PhoneCallSourceType
}

export const PhoneLogLevelSchema = createEnumSchema([
  { title: 'Trace (most verbose)', value: 'trace' },
  { title: 'Debug', value: 'debug' },
  { title: 'Info, warnings and errors', value: 'info' },
  { title: 'Warnings and errors', value: 'warn' },
  { title: 'Errors only', value: 'error' },
  { title: 'Silent (never log)', value: 'silent' },
])

export const PhoneSettingsSchema = z.object({
  callPanelPosition: CornerPositionSchema.schema
    .default('bottom-right')
    .describe('Call panel position'),

  fakeCalls: z.boolean().default(false).meta({
    description:
      'This will create calls in the database but not actually place them with Twilio.',
    title: 'Fake calls',
  }),

  logLevel: PhoneLogLevelSchema.schema
    .default(getDefaultPhoneLogLevel('production'))
    .describe('Logging level'),

  microphoneDeviceId: z.string().nullable().describe('Microphone'),

  speakerDeviceId: z.string().nullable().describe('Speaker'),
})

export type PhoneSettings = z.infer<typeof PhoneSettingsSchema>

export function getDefaultPhoneLogLevel(environment: JoyfulAppEnvironment) {
  return environment === 'development' ? 'debug' : 'error'
}

interface PhoneContextValue {
  initiatePhoneCall: UseMutationResult<void, Error, InitiatePhoneCallVariables>
  phoneSettings: PhoneSettings
  phoneState: PhoneState
  setPhoneSettings: Dispatch<SetStateAction<PhoneSettings>>
}

export const PhoneContext =
  createNonNullableContext<PhoneContextValue>('PhoneContext')

export const usePhone = PhoneContext.useRequiredContext
export const useSafePhone = PhoneContext.useSafeContext
